import { defineStore } from "pinia";
import { ExaminationTask } from "./entity";
import { ExaminationTaskList } from "./list.gen";
import type {
  ExaminationTaskStore,
  ExaminationTaskStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const examinationTaskList = new ExaminationTaskList();
export const useExaminationTaskStore = defineStore("examinationTaskStore", {
  state: (): ExaminationTaskStoreState => ({
    activeID: null,
    cache: {},
    list: [] as ExaminationTask[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-ExaminationTask", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.ExaminationTask"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(examinationTask: ExaminationTask): Promise<void> {
      // Set active
      await examinationTask
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = examinationTask.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(examinationTask: ExaminationTask): void {
      const id = examinationTask.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(examinationTask);

        // Set active
        if (this.active === null) {
          this.activeID = examinationTask.id;
        }
      } else {
        this.reinsert(examinationTask);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as ExaminationTask[]).map((x: any) => {
        const examinationTask = new ExaminationTask({});
        examinationTask.set(x as JSONData);
        return examinationTask;
      });
    },
    async delete(examinationTask: ExaminationTask): Promise<void> {
      await examinationTask
        .delete()
        .then(() => {
          this.remove(examinationTask);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const examinationTask = (this.list as ExaminationTask[]).find(
        (x: ExaminationTask) => x.id === id,
      );
      if (examinationTask !== undefined) {
        await this.delete(examinationTask);
      } else {
        throw new Error("Could not find ExaminationTask with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as ExaminationTask[]).some(
        (x: ExaminationTask) => x.id === id,
      );
    },
    get(id: string): ExaminationTask | null {
      return (
        (this.list as ExaminationTask[]).find(
          (x: ExaminationTask) => x.id === id,
        ) || null
      );
    },
    reinsert(examinationTask: ExaminationTask): void {
      const index = (this.list as ExaminationTask[]).findIndex(
        (x: ExaminationTask) => x.id === examinationTask.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          examinationTask,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(examinationTask);
      }
      this.updateKey += 1;
    },
    remove({ id }: ExaminationTask): void {
      const index = (this.list as ExaminationTask[]).findIndex(
        (x: ExaminationTask) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<ExaminationTask> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const examinationTask = this.get(id);
        if (examinationTask !== null) {
          return examinationTask;
        }
      }

      // Retrieve examinationTask
      const examinationTask = new ExaminationTask({});

      // Get examinationTask
      await examinationTask.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate examinationTask
        await this.activate(entity);
      });

      return examinationTask;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: ExaminationTask[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as ExaminationTask[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await examinationTaskList.getAll().then(async (list) => {
        list.forEach((examinationTask) => {
          this.reinsert(examinationTask);
        });

        // Activate examinationTask
        if (list.length > 0) {
          if (examinationTaskList.active !== null) {
            this.activeID = examinationTaskList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("examination_task_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as ExaminationTask[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: ExaminationTask[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as ExaminationTask,
          ),
        };
      }

      // Retrieve list
      const items = await examinationTaskList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((examinationTask) => {
            this.reinsert(examinationTask);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: ExaminationTask) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: ExaminationTask[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as ExaminationTask,
          ),
        };
      }

      // Retrieve list
      const items = await examinationTaskList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((examinationTask) => {
            this.reinsert(examinationTask);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: ExaminationTask) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: ExaminationTask[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map(
            (id: string) => this.get(id) as ExaminationTask,
          ),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await examinationTaskList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((examinationTask) => {
            this.reinsert(examinationTask);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: ExaminationTask) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(examinationTask: ExaminationTask): Promise<string> {
      // Update examinationTask
      await examinationTask.update();

      // Reinsert examinationTask
      this.reinsert(examinationTask);

      // Activate examinationTask
      if (this.active === null) {
        await this.activate(examinationTask);
      }

      this.emitStoreUpdateEvent();

      return examinationTask.id;
    },
  },
  getters: {
    active(): ExaminationTask | null {
      if (this.activeID === null) {
        return null;
      }

      const examinationTask = (this.list as ExaminationTask[]).find(
        (x: ExaminationTask) => x.id === this.activeID,
      );
      if (examinationTask === undefined) {
        return null;
      }

      return examinationTask;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createExaminationTaskStore(): ExaminationTaskStore {
  const store = useExaminationTaskStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as ExaminationTaskStore; // TODO: Fix this
}
